import dayjs from 'dayjs';
import AdvancedFormat from 'dayjs/plugin/advancedFormat';
import CauConfirmationScreen from '~/components/members-area/cau/CauConfirmationScreen.vue';
import CauPlaidLinkScreen from '~/components/members-area/cau/CauPlaidLinkScreen.vue';
import DisclosureModal from '~/components/members-area/DisclosureModal.vue';
import LoadingOverlay from '~/components/common/LoadingOverlay.vue';
import { generateMetaHead } from '~/utils/head';
import { isCauMissingPlaidLinkError, openCauPlaidLink, submitCauPlaidLink } from '~/utils/cau';
export default {
  name: 'CAU',
  components: {
    CauConfirmationScreen,
    CauPlaidLinkScreen,
    DisclosureModal,
    LoadingOverlay
  },
  layout: 'empty',
  data() {
    return {
      loading: true,
      disclosureFile: '',
      showDisclosure: false,
      showSuccess: false,
      showSuccessFadeIn: false,
      showError: false,
      showBankLinkRequired: false,
      plaidProcessing: false,
      plaidError: '',
      fromApp: false,
      memberProfile: {},
      metadata: {},
      baseUrl: this.$config.apiBaseUrl,
      token: ''
    };
  },
  head() {
    return generateMetaHead({
      title: 'Card Member Agreements | Extra',
      description: "We've updated our agreements, please take a look and agree to continue.",
      htmlAttrs: {
        class: 'theme-light'
      }
    });
  },
  computed: {
    source() {
      return this.token ? 'cau-link' : 'cau-app';
    },
    canLinkPlaidOnWeb() {
      return true;
    }
  },
  async mounted() {
    var _window;
    const {
      token,
      from_app
    } = this.$route.query;
    this.token = token || window.sessionStorage.getItem('EXTRA_CAU_TOKEN');
    this.$router.replace({
      query: {
        token: undefined
      }
    });
    if (this.token) {
      window.sessionStorage.setItem('EXTRA_CAU_TOKEN', this.token);
    }
    this.fromApp = !!((_window = window) !== null && _window !== void 0 && _window.sessionStorage.getItem('EXTRA_FROM_APP') || from_app);
    const oauthURI = window.localStorage.getItem('plaid_oauth_uri');
    if (oauthURI) {
      window.localStorage.removeItem('plaid_oauth_uri');
      this.showBankLinkRequired = true;
      this.loading = false;
      await this.startCauPlaidLink({
        oauthURI
      });
      return;
    }
    const data = await this.fetchPolicies(this.token);
    if (!data) {
      return;
    }
    this.$analyticsHub.identify(data.memberProfile.id, {
      user_id: data.memberProfile.extra_user_id
    });
    if (this.token) {
      this.$analyticsHub.track('[Single Page CAU][Web] CAU token authenticated link visit');
    }
    if (!data.hasIncompleteAgreement) {
      this.$analyticsHub.track('[Single Page CAU][Web] User already completed agreements', {
        source: this.source
      });
      this.postAgreementRedirect();
    }
  },
  methods: {
    async fetchPolicies(token) {
      try {
        const requestConfig = token ? {
          method: 'POST',
          url: `${this.baseUrl}/users/me/cau/policies`,
          headers: {
            authorization: `Bearer CAU:${token}`
          }
        } : {
          method: 'GET',
          url: '/api/members/cau/policies'
        };
        const {
          data
        } = await this.$axios.request(requestConfig);
        if (token) {
          data.memberProfile = {
            id: data.mid,
            extra_user_id: data.id,
            patriot: data.patriot
          };
        }
        this.memberProfile = data.memberProfile;
        dayjs.extend(AdvancedFormat);
        this.metadata = {
          ...data.policyMetadata,
          current_date: dayjs().format('MMMM D, YYYY'),
          process_day: dayjs(data.policyMetadata.process_day).format('Do'),
          process_month: data.policyMetadata.billing_cycle === 'annual' ? `${dayjs(data.policyMetadata.process_day).format('MMMM')} each year` : 'each month',
          formatted_plan_cost: data.policyMetadata.plan_cost.toFixed(2),
          formatted_process_date: dayjs(data.policyMetadata.process_day).format('MMMM Do, YYYY'),
          source: this.source
        };
        this.loading = false;
        this.showBankLinkRequired = false;
        this.plaidError = '';
        return data;
      } catch (err) {
        if (isCauMissingPlaidLinkError(err)) {
          this.$analyticsHub.track('[Single Page CAU][Web] Missing plaid link blocked', {
            source: this.source,
            from_app: this.fromApp,
            cau_error_flag: 'missing_plaid_link'
          });
          this.showBankLinkRequired = true;
          this.loading = false;
          return;
        }
        this.$sentry.captureException(err);
        this.showError = true;
        if (this.fromApp) {
          this.loading = false;
        } else {
          this.postAgreementRedirect();
        }
      }
    },
    async submitAgreements(token) {
      try {
        const requestConfig = token ? {
          method: 'POST',
          url: `${this.baseUrl}/users/me/cau/agreements`,
          headers: {
            authorization: `Bearer CAU:${token}`
          }
        } : {
          method: 'POST',
          url: '/api/members/cau/agreements'
        };
        await this.$axios.request(requestConfig);
        return true;
      } catch (err) {
        if (isCauMissingPlaidLinkError(err)) {
          this.$analyticsHub.track('[Single Page CAU][Web] Missing plaid link blocked', {
            source: this.source,
            from_app: this.fromApp,
            cau_error_flag: 'missing_plaid_link'
          });
          this.showBankLinkRequired = true;
          return false;
        }
        this.$sentry.captureException(err);
        this.showError = true;
        return false;
      } finally {
        this.loading = false;
      }
    },
    async startCauPlaidLink({
      oauthURI
    } = {}) {
      if (!this.canLinkPlaidOnWeb) {
        return;
      }
      this.plaidProcessing = true;
      this.plaidError = '';
      this.showBankLinkRequired = true;
      this.loading = false;
      this.$analyticsHub.track('[Single Page CAU][Web] Web plaid link started', {
        source: this.source,
        from_app: this.fromApp,
        cau_error_flag: 'missing_plaid_link'
      });
      try {
        await openCauPlaidLink({
          axios: this.$axios,
          baseUrl: this.baseUrl,
          token: this.token,
          oauthURI,
          onSuccess: this.onCauPlaidSuccess,
          onExit: this.onCauPlaidExit
        });
      } catch (err) {
        this.plaidProcessing = false;
        this.plaidError = 'Something went wrong connecting your bank. Please try again.';
        this.$sentry.captureException(err);
        this.$analyticsHub.track('[Single Page CAU][Web] Web plaid link failed', {
          source: this.source,
          from_app: this.fromApp,
          cau_error_flag: 'missing_plaid_link'
        });
      }
    },
    async onCauPlaidSuccess(publicToken, metadata) {
      this.plaidProcessing = true;
      this.plaidError = '';
      try {
        await submitCauPlaidLink({
          axios: this.$axios,
          baseUrl: this.baseUrl,
          token: this.token,
          publicToken,
          accountId: metadata.account_id
        });
        this.$analyticsHub.track('[Single Page CAU][Web] Web plaid link completed', {
          source: this.source,
          from_app: this.fromApp,
          cau_error_flag: 'missing_plaid_link'
        });
        this.loading = true;
        this.showBankLinkRequired = false;
        const data = await this.fetchPolicies(this.token);
        if (!data) {
          return;
        }
        if (!data.hasIncompleteAgreement) {
          this.postAgreementRedirect();
        }
      } catch (err) {
        var _err$response, _err$response2;
        this.plaidError = (err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 || (_err$response = _err$response.data) === null || _err$response === void 0 ? void 0 : _err$response.technicalError) || (err === null || err === void 0 || (_err$response2 = err.response) === null || _err$response2 === void 0 || (_err$response2 = _err$response2.data) === null || _err$response2 === void 0 ? void 0 : _err$response2.error) || 'We could not connect your bank. Please try again.';
        this.$sentry.captureException(err);
        this.$analyticsHub.track('[Single Page CAU][Web] Web plaid link failed', {
          source: this.source,
          from_app: this.fromApp,
          cau_error_flag: 'missing_plaid_link'
        });
      } finally {
        this.plaidProcessing = false;
      }
    },
    onCauPlaidExit(err) {
      this.plaidProcessing = false;
      if (err) {
        this.plaidError = 'Bank linking was cancelled. You can try again when ready.';
      }
    },
    viewedDisclosure() {
      this.showDisclosure = false;
    },
    showThisDisclosure(disc) {
      this.disclosureFile = disc;
      this.showDisclosure = true;
    },
    postAgreementRedirect() {
      if (this.fromApp) {
        this.$router.push('/app/dismiss');
      } else {
        this.$router.push('/download');
      }
    },
    async confirmPlan() {
      this.loading = true;
      const submitted = await this.submitAgreements(this.token);
      if (!submitted) {
        return;
      }
      this.showSuccess = true;
      this.$confetti.start();
      this.$analyticsHub.track('Single Page CAU Submitted', {
        source: this.metadata.source
      });
      window.setTimeout(() => this.showSuccessFadeIn = true, 200);
      window.setTimeout(() => {
        this.$confetti.stop();
      }, 3000);
      window.setTimeout(() => {
        this.postAgreementRedirect();
      }, 5000);
    }
  }
};